/**
 * Fades product and media-block images in as they finish loading, so an image
 * that arrives late eases in instead of popping.
 *
 * Only an image that is still loading when first seen is hidden. Cached images
 * and images that completed before this script ran are never touched, so a
 * missing `load` event (for example after a morph swaps in an already-complete
 * image) can never leave an image invisible. Styles live in
 * snippets/image-fade-styles.liquid.
 */
(() => {
  const SELECTOR = 'img.product-media__image, img.media-block__media';
  const LOADING_CLASS = 'image-fade--loading';
  const LOADED_CLASS = 'image-fade--loaded';

  /** @param {HTMLImageElement} image */
  const reveal = (image) => {
    image.classList.remove(LOADING_CLASS);
    image.classList.add(LOADED_CLASS);
  };

  /** @param {HTMLImageElement} image */
  const track = (image) => {
    if (image.complete) return;
    if (image.classList.contains(LOADING_CLASS) || image.classList.contains(LOADED_CLASS)) return;
    image.classList.add(LOADING_CLASS);
  };

  /** @param {ParentNode} root */
  const trackAll = (root) => {
    for (const image of root.querySelectorAll(SELECTOR)) {
      if (image instanceof HTMLImageElement) track(image);
    }
  };

  /** @param {Event} event */
  const onImageSettled = (event) => {
    const { target } = event;
    if (target instanceof HTMLImageElement && target.matches(SELECTOR)) reveal(target);
  };

  // `load` and `error` do not bubble, but they do pass through the capture phase.
  document.addEventListener('load', onImageSettled, true);
  document.addEventListener('error', onImageSettled, true);

  trackAll(document);

  // Images inserted later: section rendering, infinite scroll, combined-listing morphs.
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof HTMLImageElement) {
          if (node.matches(SELECTOR)) track(node);
        } else if (node instanceof Element) {
          trackAll(node);
        }
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
